import React, { useState, useRef, useEffect } from 'react';
import { 
  Server, Mail, Network, Monitor, Users, Shield, Cloud, Lock,
  ArrowRightLeft, GraduationCap, HeartHandshake, Database, 
  Settings, GitMerge, Workflow, BookOpen, Terminal, Cog, Boxes,
  AlertTriangle, Video, Presentation, Building, BarChart
} from 'lucide-react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';

const MegaMenu = () => {
  const { t } = useTranslatedContent('common');
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const menuItems = [
    {
      id: 'solutions',
      label: t('menu.solutions'),
      sections: [
        {
          title: t('menu.products.title'),
          items: [
            {
              icon: Mail,
              label: t('menu.products.zimbra'),
              description: t('menu.products.zimbraDesc'),
              href: '#scale365'
            },
            {
              icon: Shield,
              label: t('menu.products.lemonldap'),
              description: t('menu.products.lemonldapDesc'),
              href: '#lemonldap'
            },
            {
              icon: Cloud,
              label: t('menu.products.nextcloud'),
              description: t('menu.products.nextcloudDesc'),
              href: '#nextcloud'
            },
            {
              icon: Users,
              label: t('menu.products.openldap'),
              description: t('menu.products.openldapDesc'),
              href: '#openldap'
            },
            {
              icon: Video,
              label: t('menu.products.meeting'),
              description: t('menu.products.meetingDesc'),
              href: '#meeting'
            }
          ]
        }
      ]
    },
    {
      id: 'infrastructure',
      label: t('menu.infrastructure.title'),
      sections: [
        {
          title: t('menu.infrastructure.title'),
          items: [
            {
              icon: Server,
              label: t('menu.infrastructure.virtualization'),
              description: t('menu.infrastructure.virtualizationDesc'),
              href: '#infrastructure'
            },
            {
              icon: Mail,
              label: t('menu.infrastructure.email'),
              description: t('menu.infrastructure.emailDesc'),
              href: '#mailgateway'
            },
            {
              icon: Shield,
              label: t('menu.infrastructure.network'),
              description: t('menu.infrastructure.networkDesc'),
              href: '#network'
            },
            {
              icon: Monitor,
              label: t('menu.infrastructure.remote'),
              description: t('menu.infrastructure.remoteDesc'),
              href: '#remote'
            }
          ]
        }
      ]
    },
    {
      id: 'services',
      label: t('menu.services.title'),
      sections: [
        {
          title: t('menu.services.title'),
          items: [
            {
              icon: ArrowRightLeft,
              label: t('menu.services.zimbra'),
              description: t('menu.services.zimbraDesc'),
              href: '#migration'
            },
            {
              icon: Server,
              label: t('menu.services.vmware'),
              description: t('menu.services.vmwareDesc'),
              href: '#vmware-migration'
            },
            {
              icon: GraduationCap,
              label: t('menu.services.training'),
              description: t('menu.services.trainingDesc'),
              href: '#training'
            },
            {
              icon: HeartHandshake,
              label: t('menu.services.support'),
              description: t('menu.services.supportDesc'),
              href: '#support'
            }
          ]
        }
      ]
    },
    {
      id: 'training',
      label: t('menu.training.title'),
      sections: [
        {
          title: t('menu.training.administrator.title'),
          items: [
            {
              icon: Terminal,
              label: t('menu.training.administrator.zimbra.title'),
              description: t('menu.training.administrator.zimbra.desc'),
              href: '#training'
            },
            {
              icon: Shield,
              label: t('menu.training.administrator.lemonldap.title'),
              description: t('menu.training.administrator.lemonldap.desc'),
              href: '#training'
            },
            {
              icon: Cloud,
              label: t('menu.training.administrator.nextcloud.title'),
              description: t('menu.training.administrator.nextcloud.desc'),
              href: '#training'
            },
            {
              icon: Database,
              label: t('menu.training.administrator.openldap.title'),
              description: t('menu.training.administrator.openldap.desc'),
              href: '#training'
            }
          ]
        },
        {
          title: t('menu.training.infrastructure.title'),
          items: [
            {
              icon: Server,
              label: t('menu.training.infrastructure.proxmox.title'),
              description: t('menu.training.infrastructure.proxmox.desc'),
              href: '#training'
            },
            {
              icon: Boxes,
              label: t('menu.training.infrastructure.itsm.title'),
              description: t('menu.training.infrastructure.itsm.desc'),
              href: '#training'
            },
            {
              icon: Shield,
              label: t('menu.training.infrastructure.opnsense.title'),
              description: t('menu.training.infrastructure.opnsense.desc'),
              href: '#training'
            },
            {
              icon: Users,
              label: t('menu.training.infrastructure.fusion.title'),
              description: t('menu.training.infrastructure.fusion.desc'),
              href: '#training'
            }
          ]
        }
      ]
    },
    {
      id: 'cloud',
      label: t('menu.cloud.title'),
      sections: [
        {
          title: t('menu.cloud.title'),
          items: [
            {
              icon: Mail,
              label: t('menu.cloud.email'),
              description: t('menu.cloud.emailDesc'),
              href: '#saas-zimbra'
            },
            {
              icon: Lock,
              label: t('menu.cloud.sso'),
              description: t('menu.cloud.ssoDesc'),
              href: '#saas-sso'
            },
            {
              icon: Cloud,
              label: t('menu.cloud.storage'),
              description: t('menu.cloud.storageDesc'),
              href: '#saas-nextcloud'
            },
            {
              icon: Video,
              label: t('menu.cloud.meeting'),
              description: t('menu.cloud.meetingDesc'),
              href: '#meeting'
            }
          ]
        }
      ]
    },
    {
      id: 'news',
      label: t('menu.news.title'),
      sections: [
        {
          title: t('menu.news.title'),
          items: [
            {
              icon: AlertTriangle,
              label: t('menu.news.importantNotice'),
              description: t('menu.news.importantNoticeDesc'),
              href: '#whatsnew'
            },
            {
              icon: Server,
              label: t('menu.news.vmware'),
              description: t('menu.news.vmwareDesc'),
              href: '#vmware-migration'
            }
          ]
        }
      ]
    }
  ];

  const handleMenuClick = (menuId: string) => {
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  const handleItemClick = (href: string) => {
    setActiveMenu(null);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center" ref={menuRef}>
          {menuItems.map((item) => (
            <div key={item.id} className="relative group">
              <button 
                className={`
                  px-6 py-4 text-gray-600 hover:text-blue-600 font-medium relative
                  after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 
                  after:bg-blue-600 after:scale-x-0 hover:after:scale-x-100 
                  after:transition-transform after:duration-300
                  ${activeMenu === item.id ? 'text-blue-600 after:scale-x-100' : ''}
                `}
                onClick={() => handleMenuClick(item.id)}
              >
                {item.label}
              </button>
              
              {activeMenu === item.id && (
                <div 
                  className="fixed left-0 right-0 w-full z-50 animate-fade-in"
                  style={{ top: '100%' }}
                >
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                      <div className="p-8">
                        {item.sections.map((section, sectionIndex) => (
                          <div key={sectionIndex}>
                            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6">
                              {section.title}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                              {section.items.map((menuItem, itemIndex) => (
                                <button
                                  key={itemIndex}
                                  onClick={() => handleItemClick(menuItem.href)}
                                  className="flex items-start group text-left transition-transform duration-300 hover:-translate-y-1"
                                >
                                  <div className="flex-shrink-0">
                                    <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors duration-200">
                                      <menuItem.icon className="h-6 w-6 text-blue-600" />
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                                      {menuItem.label}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-200">
                                      {menuItem.description}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Backdrop */}
      {activeMenu && (
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40"
          onClick={() => setActiveMenu(null)}
        />
      )}
    </nav>
  );
};

export default MegaMenu;